@file:OptIn(ExperimentalUnsignedTypes::class)

import ai.hypergraph.kaliningraph.parsing.*
import ai.hypergraph.kaliningraph.repair.*
import ai.hypergraph.kaliningraph.tokenizeByWhitespace
import ai.hypergraph.tidyparse.wgpu.*
import ai.hypergraph.tidyparse.wgpu.GPUBufferUsage.STCPSD
import ai.hypergraph.tidyparse.wgpu.Shader.Companion.GPUBuffer
import js.buffer.ArrayBuffer
import js.typedarrays.Int32Array
import kotlinx.browser.*
import kotlinx.coroutines.*
import org.w3c.dom.*
import org.w3c.dom.events.KeyboardEvent
import org.w3c.fetch.RequestInit
import kotlin.time.TimeSource

/**
TODO (soon):
 - Rank results by more sensible metric
 - Improve support for incrementalization
 *//*
TODO (maybe):
 - Auto-alignment of the productions
 - Calculate finger-travel distance
 - Collect telemetry for a user study
 - Look into ropes, zippers and lenses
   - http://strictlypositive.org/diff.pdf
   - https://www.scs.stanford.edu/11au-cs240h/notes/zipper.html
   - https://www.st.cs.uni-saarland.de/edu/seminare/2005/advanced-fp/docs/huet-zipper.pdf
   - http://blog.ezyang.com/2010/04/you-could-have-invented-zippers/
*/
val parser = Parser(
  "whitespace" to "\\s+",
//  "red"        to "->|\\|",
  "blue"       to "---",
  "gray"       to "->|_|\\|",
  "green"      to "START",
  "other"      to "\\S"
  // Uncomment or add more rules as needed
  // "orange" to "orange",
  // "yellow" to "yellow",
  // "indigo" to "indigo",
  // "violet" to "violet",
)

// ./gradlew :tidyparse-web:jsBrowserDevelopmentRun --continuous
fun main() {
  configureRepairReranker(::browserRerankerWeights)
  configureWgpuRuntime(::log, ::markWebGpuStatus)
  MainScope().launch {
    try {
      if (window["REPAIR_MODE"] == "headless") headlessSetup()
      else if (window["REPAIR_MODE"] == "jcef") jcefSetup()
      else if (window["PROGRAMMING_LANG"] == "cnf") cnfSetup()
      else if (window["PROGRAMMING_LANG"] == "python") pythonSetup()
      else defaultSetup()
    } catch (t: Throwable) {
      if (window["REPAIR_MODE"] == "jcef") jcefSend("__TIDYPARSE_ERROR__${t.message ?: t.toString()}")
      else throw t
    }
  }
}

private val jcefScope = MainScope()
private fun jcefSend(s: String) { window.asDynamic().__tidyparseJcefSend(s) }
suspend fun jcefSetup() {
  log("Starting TidyPython (JCEF)…")

  val cfg = pythonStatementCNFAllProds
  val jcefNgrams: MutableMap<List<String>, Double> = mutableMapOf()
  loadNgrams(target = jcefNgrams)
  LED_BUFFER = 2
  TIMEOUT_MS = 1000
  tryBootstrappingGPU(needsExtraMemory = true)
  if (gpuAvailable) {
    ngrams = jcefNgrams.toGpuHash(cfg = cfg).loadToGPUBuffer()
    loadWDFA()
    neuralRerankerEnabled = true
    RepairReranker.preload()
  }
  log("Bootstrapped GPU")

  jsPyEditor.initPyodide()

  window.asDynamic().__tidyparseRunString = { line: String ->
    jcefScope.launch {
      try { jcefSend(repairPythonLineRaw(cfg, line)) }
      catch (t: Throwable) { jcefSend("__TIDYPARSE_ERROR__${t.message ?: t.toString()}") }
    }
    Unit
  }

  jcefSend("READY")
  log("JCEF runtime ready")
}

suspend fun repairPythonLineRaw(cfg: CFG, line: String, maxResults: Int = 50): String =
  JSTidyPyEditor.handleInput(line, cfg, maxResults).joinToString("\n")

suspend fun headlessSetup() {
  log("Starting Tidyparse (headless)…")

  val cfg = vanillaS2PCFG
  tryBootstrappingGPU(needsExtraMemory = true)
  log("Bootstrapped GPU")

  var errors = 0
  val es = EventSource("/stream")
  es.onmessage = { ev ->
    MainScope().launch {
      errors = 0
      val prompt = (ev.data as String).also { log("Received prompt: $it") }.tokenizeByWhitespace()
//      val out = repairCode(cfg, prompt, LED_BUFFER, ngramTensor) // With reranking + truncation
      val out = repairCode(cfg, prompt, LED_BUFFER) // Without reranking + truncation
        .distinct().joinToString("\n")
      window.fetch("/result", RequestInit(method = "POST", body = out)).await()
    }
  }
  es.onerror = { if (errors++ > 20) es.close() }
}

var lastCaretStart = 0
var lastCaretEnd = 0

private val cmEditor: dynamic
  get() = window.asDynamic().cmEditor

private fun hasCmEditor(): Boolean = cmEditor != null && cmEditor != js("undefined")

suspend fun defaultSetup() {
  log("Starting Tidyparse/CFG")
  initTidyCodeMirror()
  initSplitLayout { jsEditor.run { continuation { handleInput() } } }
  inputField.scrollTop = inputField.scrollHeight.toDouble();

  if (!fetchUrlExample()) fetchSelectedExample()
  jsEditor.getLatestCFG()
  jsEditor.redecorateLines()
  LED_BUFFER = ledBuffSel.value.toInt()
  TIMEOUT_MS = timeout.value.toInt()
  jsEditor.epsilons = epscheck.checked
  jsEditor.ntStubs = ntscheck.checked
  lastCaretStart = inputField.asDynamic().selectionStart as Int
  lastCaretEnd = inputField.asDynamic().selectionEnd as Int

  inputField.addEventListener("selectionchange", {
    val start = inputField.asDynamic().selectionStart as Int
    val end = inputField.asDynamic().selectionEnd as Int
    val multi = start != end

    if (multi || start == lastCaretStart && end == lastCaretEnd) return@addEventListener

    lastCaretStart = start
    lastCaretEnd = end
    jsEditor.run { continuation { handleInput() } }
  })

  inputField.addEventListener("input", { jsEditor.run { continuation { handleInput() } } })
  inputField.addEventListener("input", { jsEditor.redecorateLines() })
  exSelector.addEventListener("change", { MainScope().launch {
    jsEditor.restoreInstructions()
    fetchSelectedExample()
    lastCaretStart = inputField.asDynamic().selectionStart as Int
    lastCaretEnd = inputField.asDynamic().selectionEnd as Int
    jsEditor.getLatestCFG()
    jsEditor.redecorateLines()
  } })
  window.addEventListener("hashchange", { MainScope().launch {
    if (fetchUrlExample()) {
      lastCaretStart = inputField.asDynamic().selectionStart as Int
      lastCaretEnd = inputField.asDynamic().selectionEnd as Int
      jsEditor.getLatestCFG()
      jsEditor.redecorateLines()
    }
  }})

  if (hasCmEditor()) cmEditor.on("keydown") { _: dynamic, event: dynamic -> jsEditor.navUpdate(event) }
  else inputField.addEventListener("keydown", { event -> jsEditor.navUpdate(event as KeyboardEvent) })
  epscheck.addEventListener("change", {
    jsEditor.discardSoftTerminalCompletion()
    log("Changed check")
    jsEditor.epsilons = epscheck.checked
    log("Checked: ${jsEditor.epsilons}")
  })
  ntscheck.addEventListener("change", {
    jsEditor.discardSoftTerminalCompletion()
    jsEditor.ntStubs = ntscheck.checked
    try {
      jsEditor.cfg = jsEditor.getGrammarText().parseCFG(validate = true)
        .let { if (ntscheck.checked) it else it.noNonterminalStubs }
    } catch (_: Exception) {}
    jsEditor.redecorateLines()
  })
  ledBuffSel.addEventListener("change", { LED_BUFFER = ledBuffSel.value.toInt() })
  timeout.addEventListener("change", { TIMEOUT_MS = timeout.value.toInt() })

  tryBootstrappingGPU()
}

suspend fun pythonSetup() {
  log("Starting TidyPython")

  initPythonCodeMirror()
  initNeuralRerankerControl()
  jsPyEditor.getLatestCFG()
//    LED_BUFFER = maxEdits.value.toInt()
  jsPyEditor.redecorateLines()

  loadNgrams()
  MainScope().launch {
    val t0 = TimeSource.Monotonic.markNow()
    try {
      tryBootstrappingGPU(true)
      if (gpuAvailable) {
        loadPythonPCFG(jsPyEditor.getLatestCFG())
        ngrams = jsPyEditor.ngrams.toGpuHash(cfg = jsPyEditor.getLatestCFG()).loadToGPUBuffer()
        log("Loaded n-grams into ${jsPyEditor.ngramTensor.size / 1000000}mb GPU buffer in ${t0.elapsedNow()}")
        loadWDFA()
        markNeuralRerankerAvailable()
      } else {
        markNeuralRerankerUnavailable("Neural reranker unavailable: WebGPU unavailable")
      }
    } catch (t: Throwable) {
      markNeuralRerankerUnavailable("Neural reranker unavailable: ${t.message ?: t.toString()}")
      log("Failed to initialize GPU/reranker path: ${t.message ?: t}")
    }
  }

  TIMEOUT_MS = 1000

  inputField.addEventListener("input", { jsPyEditor.run { continuation { handleInput() } } })
  inputField.addEventListener("input", { jsPyEditor.redecorateLines() })
  jsPyEditor.cme.on("keydown") { _: dynamic, e: dynamic -> jsPyEditor.navUpdate(e) }

//  jsPyEditor.minimize = mincheck.checked
//  mincheck.addEventListener("change", { jsPyEditor.minimize = mincheck.checked })
  LED_BUFFER = ledBuffSel.value.toInt()
  ledBuffSel.addEventListener("change", { LED_BUFFER = ledBuffSel.value.toInt() })

  jsPyEditor.initPyodide()
}

private const val NEURAL_RERANKER_CHECKBOX_ID = "neural-reranker-checkbox"
private const val NEURAL_RERANKER_LABEL_ID = "neural-reranker-label"
private const val NEURAL_RERANKER_STATUS_ID = "neuralRerankerStatus"
private var neuralRerankerControlBound = false
private var neuralRerankerLoaded = false
private var neuralRerankerLoading = false

private fun initNeuralRerankerControl() {
  neuralRerankerEnabled = false
  setNeuralRerankerSelectable(false, "Neural reranker waiting for WebGPU")
  markNeuralRerankerStatus("pending", "Neural reranker waiting for WebGPU")

  if (neuralRerankerControlBound) return
  val checkbox = document.getElementById(NEURAL_RERANKER_CHECKBOX_ID) as? HTMLInputElement ?: return

  checkbox.addEventListener("change", change@{
    if (neuralRerankerLoading) {
      checkbox.checked = true
      return@change
    }

    if (!checkbox.checked) {
      neuralRerankerEnabled = false
      jsPyEditor.run { continuation { handleInput() } }
      return@change
    }

    if (neuralRerankerLoaded) {
      neuralRerankerEnabled = true
      jsPyEditor.run { continuation { handleInput() } }
      return@change
    }

    neuralRerankerEnabled = false
    markNeuralRerankerLoading("Neural reranker weights loading")
    checkbox.checked = true
    neuralRerankerLoading = true

    MainScope().launch {
      try {
        if (RepairReranker.preloadAvailable()) {
          neuralRerankerLoaded = true
          markNeuralRerankerReady()
          checkbox.checked = true
          neuralRerankerEnabled = true
        } else {
          markNeuralRerankerRetryable()
        }
      } finally {
        neuralRerankerLoading = false
      }
      jsPyEditor.run { continuation { handleInput() } }
    }
  })
  neuralRerankerControlBound = true
}

private fun markNeuralRerankerAvailable() {
  val label = "Neural reranker ready to load on demand"
  setNeuralRerankerSelectable(true, label)
  markNeuralRerankerStatus("pending", label)
}

private fun markNeuralRerankerLoading(label: String) {
  setNeuralRerankerSelectable(false, label)
  markNeuralRerankerStatus("warming", label)
}

private fun markNeuralRerankerReady() {
  setNeuralRerankerSelectable(true, "Neural reranker weights loaded")
  markNeuralRerankerStatus("ready", "Neural reranker weights loaded")
}

private fun markNeuralRerankerRetryable() {
  val label = "Neural reranker failed to load; enable to retry"
  neuralRerankerEnabled = false
  setNeuralRerankerSelectable(true, label)
  (document.getElementById(NEURAL_RERANKER_CHECKBOX_ID) as? HTMLInputElement)?.checked = false
  markNeuralRerankerStatus("error", label)
}

private fun markNeuralRerankerUnavailable(label: String) {
  neuralRerankerEnabled = false
  setNeuralRerankerSelectable(false, label)
  markNeuralRerankerStatus("error", label)
}

private fun setNeuralRerankerSelectable(selectable: Boolean, label: String) {
  val checkbox = document.getElementById(NEURAL_RERANKER_CHECKBOX_ID) as? HTMLInputElement ?: return
  checkbox.disabled = !selectable
  if (!selectable) checkbox.checked = false
  checkbox.setAttribute("title", label)

  val labelNode = document.getElementById(NEURAL_RERANKER_LABEL_ID) as? HTMLElement ?: return
  labelNode.setAttribute("title", label)
  if (selectable) labelNode.classList.remove("is-disabled")
  else labelNode.classList.add("is-disabled")
}

private fun markNeuralRerankerStatus(state: String, label: String) {
  val node = document.getElementById(NEURAL_RERANKER_STATUS_ID) as? HTMLElement ?: return
  node.classList.remove("pending")
  node.classList.remove("warming")
  node.classList.remove("ready")
  node.classList.remove("error")
  node.classList.add(state)
  node.setAttribute("aria-label", label)
  node.setAttribute("title", label)
}

private fun markWebGpuStatus(state: String, label: String) {
  val node = document.getElementById("gpuAvail") as? HTMLDivElement ?: return
  node.classList.remove("pending")
  node.classList.remove("warming")
  node.classList.remove("ready")
  node.classList.remove("error")
  node.classList.add(state)
  node.setAttribute("aria-label", label)
  node.setAttribute("title", label)

  if (state == "ready" && node.querySelector("object") == null) {
    node.appendChild(document.createElement("object").apply {
      setAttribute("type", "image/svg+xml")
      setAttribute("data", "/webgpu.svg")
      setAttribute("width", "24")
      setAttribute("height", "24")
    })
  }
}

val exSelector by lazy { document.getElementById("ex-selector") as HTMLSelectElement }
val decorator by lazy { TextareaDecorator(inputField, parser) }
val pyDecorator by lazy { PyTextareaDecorator(jsPyEditor.cme) }
val jsEditor by lazy { JSTidyEditor(inputField, outputField) }
val jsPyEditor by lazy { JSTidyPyEditor(inputField, outputField) }
val inputField by lazy { document.getElementById("tidyparse-input") as HTMLTextAreaElement }
val outputField by lazy { document.getElementById("tidyparse-output") as Node }
val epscheck by lazy { document.getElementById("epsilon-checkbox") as HTMLInputElement }
val ntscheck by lazy { document.getElementById("ntstubs-checkbox") as HTMLInputElement }
val timeout by lazy { document.getElementById("timeout") as HTMLInputElement }
val ledBuffSel by lazy { document.getElementById("led-buffer") as HTMLInputElement }

suspend fun loadWDFA(file: String = "wdfa.bin") {
  fun loadWDFAFromArrayBuffer(ab: ArrayBuffer) {
    val ints = Int32Array(ab)
    val buf = GPUBuffer(byteSize = ints.byteLength, us = STCPSD, data = ints)

    wdfaNumStates = ints[3]
    wdfaNumEdges = ints[4]
    wdfa = buf
  }
  val t0 = TimeSource.Monotonic.markNow()
  val loaded = browserWdfa(file)
    ?: run {
      val message = "Failed to load WDFA from $file"
      log(message)
      error(message)
    }
  loadWDFAFromArrayBuffer(loaded)
  log("Loaded WDFA(|Q|=$wdfaNumStates, |δ|=$wdfaNumEdges) in ${t0.elapsedNow()}")
}

suspend fun loadNgrams(
  file: String = "python_4grams.txt",
  target: MutableMap<List<String>, Double> = jsPyEditor.ngrams
) {
  val t0 = TimeSource.Monotonic.markNow()
  val loaded = browserNgrams(file) ?: return log("Failed to load ngrams from $file")

  val n = parseNgrams(loaded, target)
  log("Loaded ${target.size} $n-grams in ${t0.elapsedNow()}")
}

suspend fun loadPythonPCFG(cfg: CFG) {
  if (!gpuAvailable) return
  val file = "python_statement_pcfg.txt"
  val response = window.fetch(browserResourceUrl(file)).await()
  if (response.ok) cfg.loadPCFG(response.text().await())
  else log("Failed to load PCFG from $file: ${response.status}")
}

private fun parseNgrams(raw: String, target: MutableMap<List<String>, Double>): Int {
  var n = 0
  raw.lines().filter { it.isNotBlank() }.forEach { line ->
    val (ngram, count) = line.split(" ::: ")
    target[ngram.split(" ").also { n = it.size }] = count.toDouble()
  }
  return n
}

private fun Element.scrollIntoViewCompat() {
  val opts = js("({ block: 'end', inline: 'nearest', behavior: 'auto' })")
  try { this.asDynamic().scrollIntoView(opts) } catch (_: dynamic) { this.scrollIntoView(false) }
}

private fun decodeUrlComponent(value: String): String =
  try { js("decodeURIComponent(value)") as String } catch (_: dynamic) { value }

private fun directExampleRequest(): String? {
  val hash = window.location.hash.removePrefix("#")
  return normalizeExamplePath(hash) ?: normalizeExamplePath(window.location.pathname)
}

private fun normalizeExamplePath(rawPath: String): String? {
  var path = decodeUrlComponent(rawPath).trim()
    .substringBefore("?")
    .substringBefore("&")
    .removePrefix("example=")
    .removePrefix("/")

  if (path.startsWith("http://") || path.startsWith("https://"))
    path = path.substringAfter("://").substringAfter("/")

  path = path.replace('\\', '/')
    .removePrefix("/")
    .removePrefix("./")
    .replace(Regex("^examples/griebach/"), "examples/greibach/")
    .replace(Regex("^griebach/"), "greibach/")

  if (!path.endsWith(".tidy") || path.split('/').any { it.isBlank() || it == ".." }) return null
  return if (path.startsWith("examples/")) path else "examples/$path"
}

private fun syncExampleSelector(examplePath: String) {
  val options = exSelector.asDynamic().options
  for (i in 0 until (options.length as Int))
    if (options[i].value == examplePath) { exSelector.value = examplePath; return }
}

private fun updateExampleHash(examplePath: String) {
  val nextHash = "#$examplePath"
  if (window.location.hash != nextHash)
    window.history.asDynamic().replaceState(null, document.title, nextHash)
}

suspend fun fetchUrlExample(): Boolean =
  fetchExample(directExampleRequest() ?: return false, syncSelector = true, updateHash = false)

suspend fun fetchSelectedExample() {
  val selectedExample = exSelector.value
  if (selectedExample.endsWith(".html")) { window.location.href = selectedExample; return }
  fetchExample(selectedExample, syncSelector = false, updateHash = true)
}

private var embeddedGzipExamples: dynamic = null

private suspend fun inlineExampleText(examplePath: String): String? {
  val inlineExamples = window.asDynamic().raw_examples
  val inlineExample = if (inlineExamples != null && inlineExamples != js("undefined")) {
    inlineExamples[examplePath] as? String
  } else null
  if (inlineExample != null) return inlineExample

  if (embeddedGzipExamples == null) {
    val inlineExamplesGzipB64 = window.asDynamic().raw_examples_gzip_b64 as? String
    if (!inlineExamplesGzipB64.isNullOrBlank()) {
      val examplesJson = gzipBase64ToText(inlineExamplesGzipB64)
      embeddedGzipExamples = js("JSON.parse(examplesJson)")
    }
  }

  return if (embeddedGzipExamples != null) embeddedGzipExamples[examplePath] as? String else null
}

suspend fun fetchExample(examplePath: String, syncSelector: Boolean, updateHash: Boolean): Boolean {
  fun applyExampleText(text: String) {
    inputField.value = text
    if (hasCmEditor()) {
      cmEditor.setValue(text)
      cmEditor.save()
      cmEditor.scrollTo(null, cmEditor.getScrollInfo().height)
    } else window.requestAnimationFrame { (inputField as Element).scrollIntoViewCompat() }
    if (syncSelector) syncExampleSelector(examplePath)
    if (updateHash) updateExampleHash(examplePath)
    jsEditor.redecorateLines()
  }

  val inlineExample = inlineExampleText(examplePath)
  if (inlineExample != null) { applyExampleText(inlineExample); return true }

  val response = window.fetch(browserResourceUrl(examplePath)).await()
  if (response.ok) { applyExampleText(response.text().await()); return true }
  else console.error("Failed to load file '$examplePath': ${response.status}")
  return false
}

internal const val MIN_DISPLAY_RESULTS = 28
internal const val DISPLAY_HEADER_LINES = 2
internal const val DISPLAY_SUMMARY_LINES = 7
private const val DISPLAY_NON_RESULT_LINES = DISPLAY_HEADER_LINES + DISPLAY_SUMMARY_LINES

internal fun displayResultLimit(availableHeightPx: Double, lineHeightPx: Double): Int =
  if (availableHeightPx.isFinite() && lineHeightPx.isFinite() && lineHeightPx > 0.0)
    ((availableHeightPx / lineHeightPx).toInt() - DISPLAY_NON_RESULT_LINES)
      .coerceAtLeast(MIN_DISPLAY_RESULTS)
  else MIN_DISPLAY_RESULTS

internal fun displayResultLimit(output: HTMLElement): Int {
  val style = window.getComputedStyle(output)
  fun pixels(property: String): Double? =
    style.getPropertyValue(property).removeSuffix("px").trim().toDoubleOrNull()

  val lineHeight = pixels("line-height") ?: pixels("font-size")?.times(1.2) ?: 0.0
  val panelHeight = output.clientHeight.toDouble()
  val viewportHeight = window.innerHeight.toDouble() - output.getBoundingClientRect().top
  if (panelHeight <= 0.0 || viewportHeight <= 0.0) return MIN_DISPLAY_RESULTS
  val visibleHeight = minOf(panelHeight, viewportHeight)
  val contentHeight = visibleHeight -
          (pixels("padding-top") ?: 0.0) - (pixels("padding-bottom") ?: 0.0)
  return displayResultLimit(contentHeight, lineHeight)
}
